import { useCallback, useEffect, useRef, useState, type DependencyList } from "react";
import { createLatestGate } from "./latest.js";

export interface AsyncState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  /** Re-run the loader (e.g. after a mutation or on "retry"). */
  reload: () => void;
  /** Replace the data locally (optimistic update / mutation result). */
  setData: (next: T) => void;
}

/**
 * Load data for the current (org, id, …): re-runs when `deps` change, and only
 * the newest run may write state — a slow response for the previous org or
 * record can never overwrite the one on screen. Unmount cancels.
 */
export function useAsync<T>(load: () => Promise<T>, deps: DependencyList): AsyncState<T> {
  const gate = useRef(createLatestGate()).current;
  const [data, setData] = useState<T | undefined>(undefined);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const token = gate.begin();
    setLoading(true);
    setError(null);
    load().then(
      (value) => {
        if (!gate.isCurrent(token)) return;
        setData(value);
        setLoading(false);
      },
      (err: unknown) => {
        if (!gate.isCurrent(token)) return;
        setError(err);
        setLoading(false);
      },
    );
    return () => gate.cancel();
    // `load` is intentionally not a dependency: callers pass an inline closure;
    // `deps` is the contract for when to reload (same as useEffect's).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, error, loading, reload, setData };
}

/**
 * Run a write with a pending flag and error capture. `run` resolves to the
 * result, or to undefined when it failed (the error is in `error`), so a click
 * handler never has an unhandled rejection. Unmounted components are not written to.
 */
export function useMutation<A extends unknown[], R>(fn: (...args: A) => Promise<R>) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mounted = useRef(true);
  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );
  const run = useCallback(
    async (...args: A): Promise<R | undefined> => {
      setPending(true);
      setError(null);
      try {
        return await fn(...args);
      } catch (err) {
        if (mounted.current) setError(err);
        return undefined;
      } finally {
        if (mounted.current) setPending(false);
      }
    },
    [fn],
  );
  return { run, pending, error };
}
